import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import PricingConstants from '@sprinx/pricing-core/constants';
import createSubjectWithoutProductResolving from '@sprinx/pricing-core/createSubjectWithoutProductResolving';
import useTranslate from '@sprinx/react-globalize/useTranslate';
import useApplyPricingRulesToCartHandler from '@sprinx/react-pricing/useApplyPricingRulesToCartHandler';
import useCreateCart from '@sprinx/react-pricing/useCreateCart';
import useCreateSubjectHandler from '@sprinx/react-pricing/useCreateSubjectHandler';
import useEffectPricingRules from '@sprinx/react-pricing/useEffectPricingRules';
import React from 'react';

var pipe = function pipe() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return function (v) {
    return fns.reduce(function (r, fn) {
      return fn(r);
    }, v);
  };
};

var cond = function cond() {
  for (var _len2 = arguments.length, cnds = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    cnds[_key2] = arguments[_key2];
  }

  return function (v) {
    return (cnds.find(function (x) {
      return x[0](v);
    }) || [null, function () {
      return undefined;
    }])[1](v);
  };
}; // TODO: validation of primary subject id should be moved to @sprinx/prod-core and imported from @sprinx/react-prod


var isValidIndirectSubjectId = PricingConstants.isValidIndirectSubjectId,
    PAYMENT_ID = PricingConstants.PAYMENT_ID,
    SHIPPING_ID = PricingConstants.SHIPPING_ID;

var isValidPrimarySubjectId = function isValidPrimarySubjectId(id) {
  return !isValidIndirectSubjectId(id) && id !== PAYMENT_ID && id !== SHIPPING_ID;
};

var updateSubjectArr = function updateSubjectArr(id, quantity, arr, isIndirect) {
  return (// eslint-disable-next-line no-use-before-define
    arr.reduce(function (nextArr, i) {
      return [].concat(_toConsumableArray(nextArr), [updateSingleSubject(id, quantity, i, isIndirect)]);
    }, []).filter(Boolean)
  );
};

function updateSingleSubject(id, quantity, subject, isIndirect) {
  return cond([function () {
    return subject.id === id && quantity <= 0;
  }, function () {
    return isIndirect ? _extends({}, subject, {
      deleted: true
    }) : null;
  }], [function () {
    return subject.id === id && quantity > 0;
  }, function () {
    return _extends({}, subject, {
      quantity: quantity
    }, subject.deleted ? {
      deleted: false
    } : {});
  }], [function () {
    return (subject.discounts || []).map(function (i) {
      return i.id;
    }).includes(id);
  }, function () {
    return _extends({}, subject, {
      discounts: updateSubjectArr(id, quantity, subject.discounts, true)
    });
  }], [function () {
    return (subject.indirectSubjects || []).map(function (i) {
      return i.id;
    }).includes(id);
  }, function () {
    return _extends({}, subject, {
      indirectSubjects: updateSubjectArr(id, quantity, subject.indirectSubjects, true)
    });
  }], [function () {
    return true;
  }, function () {
    return subject;
  }])();
}

var updateSubjects = function updateSubjects(id, quantity) {
  return function (cart) {
    return _extends({}, cart, {
      subjects: updateSubjectArr(id, quantity, cart.subjects)
    });
  };
};

var updateIndirectSubjects = function updateIndirectSubjects(id, quantity) {
  return function (cart) {
    return _extends({}, cart, {
      indirectSubjects: updateSubjectArr(id, quantity, cart.indirectSubjects, true)
    });
  };
};

var updateShipping = function updateShipping(id, quantity) {
  return function (_ref) {
    var shipping = _ref.shipping,
        rest = _objectWithoutProperties(_ref, ["shipping"]);

    return _extends({}, rest, {
      shipping: updateSingleSubject(id, quantity, shipping)
    });
  };
};

var updatePayment = function updatePayment(id, quantity) {
  return function (_ref2) {
    var payment = _ref2.payment,
        rest = _objectWithoutProperties(_ref2, ["payment"]);

    return _extends({}, rest, {
      payment: updateSingleSubject(id, quantity, payment)
    });
  };
};

var subjectArrToFindIndexReducer = function subjectArrToFindIndexReducer(isIndirect) {
  return function (ids, s) {
    return _extends({}, ids, _defineProperty({}, s.id, _extends({}, s, {
      indirect: isIndirect
    })), (s.indirectSubjects || []).reduce(function (x, y) {
      return _extends({}, x, _defineProperty({}, y.id, _extends({}, y, {
        indirect: true
      })));
    }, {}), (s.discounts || []).reduce(function (x, y) {
      return _extends({}, x, _defineProperty({}, y.id, _extends({}, y, {
        indirect: true
      })));
    }, {}));
  };
};

var buildSubjectIdsFindIndex = /*#__PURE__*/function () {
  var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(cartPromise) {
    var cart, flatCart, findIdex;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return cartPromise;

          case 2:
            cart = _context.sent;
            flatCart = _extends({}, (cart.subjects || []).reduce(subjectArrToFindIndexReducer(false), {}), _defineProperty({}, cart.payment.id, _extends({}, cart.payment, {
              indirect: true
            })), (cart.payment.discounts || []).reduce(subjectArrToFindIndexReducer(true), {}), (cart.payment.indirectSubjects || []).reduce(subjectArrToFindIndexReducer(true), {}), _defineProperty({}, cart.shipping.id, _extends({}, cart.shipping, {
              indirect: true
            })), (cart.shipping.discounts || []).reduce(subjectArrToFindIndexReducer(true), {}), (cart.shipping.indirectSubjects || []).reduce(subjectArrToFindIndexReducer(true), {}), (cart.indirectSubjects || []).reduce(subjectArrToFindIndexReducer(true), {}));
            findIdex = Object.keys(flatCart).filter(Boolean);
            return _context.abrupt("return", [cart, flatCart, findIdex]);

          case 6:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function buildSubjectIdsFindIndex(_x) {
    return _ref3.apply(this, arguments);
  };
}();

var enableIndirectSubject = function enableIndirectSubject(subjectId) {
  return function (cart) {
    return _extends({}, cart, {
      subjects: cart.subjects.map(function (s) {
        return _extends({}, s, {
          indirectSubjects: (s.indirectSubjects || []).map(function (is) {
            return is.id === subjectId ? _extends({}, is, {
              deleted: false
            }) : {};
          })
        });
      }),
      shipping: _extends({}, cart.shipping || {}, {
        indirectSubjects: ((cart.shipping || {}).indirectSubjects || []).map(function (is) {
          return is.id === subjectId ? _extends({}, is, {
            deleted: false
          }) : {};
        })
      }),
      payment: _extends({}, cart.payment || {}, {
        indirectSubjects: ((cart.payment || {}).indirectSubjects || []).map(function (is) {
          return is.id === subjectId ? _extends({}, is, {
            deleted: false
          }) : {};
        })
      }),
      indirectSubjects: (cart.indirectSubjects || []).map(function (is) {
        return is.id === subjectId ? _extends({}, is, {
          deleted: false
        }) : {};
      })
    });
  };
};

export default function useUpdateCart(showSnackbarMessage) {
  var t = useTranslate();
  var cartRef = React.useRef(null);
  var emptyCart = useCreateCart();
  var initialState = React.useMemo(function () {
    return {
      cart: emptyCart,
      flatCart: [],
      subjectFindIndex: []
    };
  }, [emptyCart]);

  var _React$useState = React.useState(initialState),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      _React$useState2$ = _React$useState2[0],
      cartInput = _React$useState2$.cart,
      flatCart = _React$useState2$.flatCart,
      subjectFindIndex = _React$useState2$.subjectFindIndex,
      setState = _React$useState2[1];

  var applyPricingRules = useApplyPricingRulesToCartHandler();
  var createSubject = useCreateSubjectHandler();
  React.useEffect(function () {
    cartRef.current = cartInput;
  }, [cartInput]);
  var storeCartData = React.useCallback(function (state) {
    if (window.localStorage) {
      window.localStorage.setItem('shopping-cart', JSON.stringify(state.cart));
    }

    setState(state);
  }, []);
  React.useEffect(function () {
    if (window.localStorage) {
      var storedCartSerialized = window.localStorage.getItem('shopping-cart');

      if (typeof storedCartSerialized === 'string') {
        var storedCart = JSON.parse(storedCartSerialized);

        if (storedCart) {
          buildSubjectIdsFindIndex(applyPricingRules(storedCart)).then(function (ret) {
            var _ret = _slicedToArray(ret, 3),
                nextCart = _ret[0],
                nextFlatCart = _ret[1],
                nextSubjectFindIndex = _ret[2];

            storeCartData({
              cart: nextCart,
              flatCart: nextFlatCart,
              subjectFindIndex: nextSubjectFindIndex
            });
          });
        }
      }
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  var resetCart = React.useCallback(function () {
    storeCartData(initialState);

    if (showSnackbarMessage) {
      showSnackbarMessage(t('shoppingCart/messages/cartManipulations/allRemoved'), {
        variant: 'info',
        preventDuplicate: true
      });
    }
  }, [initialState, showSnackbarMessage, t, storeCartData]);
  var updateCart = React.useCallback( /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(data) {
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              return _context3.abrupt("return", pipe(function (cart) {
                return (Array.isArray(data) ? data : [data]).filter(Boolean).reduce(function (xcart, _ref5) {
                  var enableIndirect = _ref5.enableIndirect,
                      product = _ref5.product,
                      quantity = _ref5.quantity,
                      subjectId = _ref5.subjectId;
                  var subjectExists = subjectFindIndex.includes(subjectId);
                  var validPrimaryId = isValidPrimarySubjectId(subjectId);
                  return cond([function () {
                    return !subjectExists && validPrimaryId && quantity > 0;
                  }, function (crt) {
                    return _extends({}, crt, {
                      subjects: [].concat(_toConsumableArray(crt.subjects), [createSubject({
                        product: product,
                        quantity: quantity
                      })])
                    });
                  }], [function () {
                    return subjectId === PAYMENT_ID;
                  }, function (crt) {
                    return _extends({}, crt, {
                      payment: _extends({}, createSubjectWithoutProductResolving({
                        product: product,
                        quantity: quantity
                      }), {
                        id: PAYMENT_ID
                      })
                    });
                  }], [function () {
                    return subjectId === SHIPPING_ID;
                  }, function (crt) {
                    return _extends({}, crt, {
                      shipping: _extends({}, createSubjectWithoutProductResolving({
                        product: product,
                        quantity: quantity
                      }), {
                        id: SHIPPING_ID
                      })
                    });
                  }], [function () {
                    return subjectExists && !enableIndirect;
                  }, pipe(updateSubjects(subjectId, quantity), updateShipping(subjectId, quantity), updatePayment(subjectId, quantity), updateIndirectSubjects(subjectId, quantity))], [function () {
                    return subjectExists && !validPrimaryId && enableIndirect;
                  }, enableIndirectSubject(subjectId)], [function () {
                    return true;
                  }, function (c) {
                    return c;
                  }])(xcart);
                }, cart);
              }, applyPricingRules, buildSubjectIdsFindIndex, /*#__PURE__*/function () {
                var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(ret) {
                  var _yield$ret, _yield$ret2, nextCart, nextFlatCart, nextSubjectFindIndex, prevCart, toShow, prevSubNum, nextSubNum, sumQ, prevSumQ, nextSumQ, show;

                  return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                    while (1) {
                      switch (_context2.prev = _context2.next) {
                        case 0:
                          _context2.next = 2;
                          return ret;

                        case 2:
                          _yield$ret = _context2.sent;
                          _yield$ret2 = _slicedToArray(_yield$ret, 3);
                          nextCart = _yield$ret2[0];
                          nextFlatCart = _yield$ret2[1];
                          nextSubjectFindIndex = _yield$ret2[2];
                          prevCart = _extends({}, cartRef.current);
                          storeCartData({
                            cart: nextCart,
                            flatCart: nextFlatCart,
                            subjectFindIndex: nextSubjectFindIndex
                          });

                          if (showSnackbarMessage) {
                            toShow = function toShow(msg) {
                              return function () {
                                return {
                                  msg: msg
                                };
                              };
                            };

                            prevSubNum = prevCart.subjects.length;
                            nextSubNum = nextCart.subjects.length;

                            sumQ = function sumQ(sbjs) {
                              return sbjs.reduce(function (sm, s) {
                                return sm + s.quantity;
                              }, 0);
                            };

                            prevSumQ = sumQ(prevCart.subjects);
                            nextSumQ = sumQ(nextCart.subjects);
                            show = cond( // kosik sa vyprazdnil
                            [function () {
                              return prevSubNum > 0 && nextSubNum === 0;
                            }, toShow(t('shoppingCart/messages/cartManipulations/allRemoved'))], // pribudol subject do kosiku
                            [function () {
                              return prevSubNum < nextSubNum;
                            }, toShow(t('shoppingCart/messages/cartManipulations/itemAdded', {
                              number: nextSubNum - prevSubNum
                            }))], // odbudol subject z kosiku
                            [function () {
                              return prevSubNum > nextSubNum;
                            }, toShow(t('shoppingCart/messages/cartManipulations/itemRemoved', {
                              number: prevSubNum - nextSubNum
                            }))], // zmenilo sa mnozstvo
                            [function () {
                              return prevSumQ !== nextSumQ;
                            }, toShow(t('shoppingCart/messages/cartManipulations/quantityChaged'))], [function () {
                              return true;
                            }, toShow(null)])();

                            if (show.msg) {
                              showSnackbarMessage(show.msg, {
                                variant: 'info',
                                preventDuplicate: true
                              });
                            }
                          }

                        case 10:
                        case "end":
                          return _context2.stop();
                      }
                    }
                  }, _callee2);
                }));

                return function (_x3) {
                  return _ref6.apply(this, arguments);
                };
              }())(cartRef.current));

            case 1:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));

    return function (_x2) {
      return _ref4.apply(this, arguments);
    };
  }(), [applyPricingRules, createSubject, subjectFindIndex, showSnackbarMessage, t, storeCartData]);

  var manipulateCartDirect = React.useCallback(function (partial) {
    const prevCart = JSON.parse(window.localStorage.getItem('shopping-cart'));
    const newCart = {...prevCart, ...partial };
    storeCartData({ cart: newCart, flatCart, subjectFindIndex});
  }, [storeCartData, flatCart, subjectFindIndex]);

  useEffectPricingRules(function () {
    updateCart();
  });
  return [cartInput, flatCart, updateCart, resetCart, manipulateCartDirect];
}